/**
 * Edit a child's profile: name, nickname, color, birth year, and avatar.
 * Mirrors the AddChild form layout, but operates on an existing row via
 * PATCH /v1/children/{id} and (separately) POST /v1/children/{id}/avatar
 * for the image binary. Avatar upload is its own request because mixing
 * JSON + multipart in one payload would force every other field through
 * base64 — small win, large complexity tax.
 *
 * Delete is offered at the bottom with a clear confirmation. It removes
 * the child row + paired devices (FK cascade). Cross-service rows
 * (location, analytics) are orphaned by design; a future sweeper reaps
 * them on a schedule.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImagePlus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { usersApi, type Child } from '@/api/users';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io';

interface EditChildDialogProps {
  child: Child | null;
  onClose: () => void;
}

export function EditChildDialog({ child, onClose }: EditChildDialogProps) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [birthYear, setBirthYear] = useState<number | ''>('');
  const [color, setColor] = useState('#FF9EC4');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Reset form whenever a different child is opened. Key on id so reopens
  // for the same child preserve in-progress edits.
  useEffect(() => {
    if (!child) return;
    setName(child.name);
    setNickname(child.nickname ?? '');
    setBirthYear(child.birth_year ?? '');
    setColor(child.color ?? '#FF9EC4');
    setPreviewUrl(child.avatar_url ? `${API_BASE}${child.avatar_url}` : null);
    setPendingFile(null);
    setConfirmDelete(false);
  }, [child?.id]);

  const update = useMutation({
    mutationFn: async () => {
      if (!child) throw new Error('no_child');
      // PATCH the fields first; avatar upload (if any) is a second call.
      const patched = await usersApi.updateChild(child.id, {
        name: name.trim(),
        nickname: nickname.trim() || null,
        birth_year: birthYear === '' ? null : Number(birthYear),
        color,
      });
      if (pendingFile) {
        return await usersApi.uploadChildAvatar(child.id, pendingFile);
      }
      return patched;
    },
    onSuccess: () => {
      toast.success('Profile updated.');
      void qc.invalidateQueries({ queryKey: ['children'] });
      onClose();
    },
    onError: (e: Error) => {
      if (e.message.includes('avatar_too_large')) {
        toast.error('Image too large (max 5 MB).');
      } else if (e.message.includes('unsupported_image_type')) {
        toast.error('Only JPG, PNG or WebP images.');
      } else {
        toast.error('Could not save changes.');
      }
    },
  });

  const removeAvatar = useMutation({
    mutationFn: () => usersApi.deleteChildAvatar(child!.id),
    onSuccess: () => {
      toast.success('Photo removed.');
      setPreviewUrl(null);
      setPendingFile(null);
      void qc.invalidateQueries({ queryKey: ['children'] });
    },
    onError: () => toast.error('Could not remove photo.'),
  });

  const remove = useMutation({
    mutationFn: () => usersApi.deleteChild(child!.id),
    onSuccess: () => {
      toast.success(`${child?.name ?? 'Child'} deleted.`);
      void qc.invalidateQueries({ queryKey: ['children'] });
      onClose();
    },
    onError: () => toast.error('Could not delete.'),
  });

  const onPickFile = (file: File) => {
    setPendingFile(file);
    // Local preview without waiting for upload.
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  if (!child) return null;

  return (
    <Dialog
      open={child !== null}
      onOpenChange={(o) => { if (!o && !update.isPending && !remove.isPending) onClose(); }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {child.name}</DialogTitle>
          <DialogDescription>
            Change name, color, birth year, or photo. Each child has their
            own profile.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="group relative grid size-20 shrink-0 place-items-center overflow-hidden rounded-full border-2 border-dashed border-[color:var(--kz-color-border-subtle)]"
              style={{ backgroundColor: previewUrl ? 'transparent' : color }}
              aria-label="Change avatar"
            >
              {previewUrl ? (
                <img src={previewUrl} alt="" className="size-full object-cover" />
              ) : (
                <span className="text-sm font-semibold text-white">
                  {name.slice(0, 2).toUpperCase()}
                </span>
              )}
              <span className="absolute inset-0 grid place-items-center bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100">
                <ImagePlus className="size-5" />
              </span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onPickFile(f);
              }}
            />
            <div className="flex-1 space-y-1.5">
              <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
                Click the circle to upload a photo. JPG, PNG, or WebP. Up to 5 MB.
              </p>
              {(previewUrl && child.avatar_url) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeAvatar.mutate()}
                  disabled={removeAvatar.isPending}
                  className="text-xs text-rose-700"
                >
                  <X className="size-3" /> Remove photo
                </Button>
              )}
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="ec-name">Name</Label>
            <Input
              id="ec-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="ec-birth">Birth year</Label>
              <Input
                id="ec-birth"
                type="number"
                min={2000}
                max={2030}
                value={birthYear}
                onChange={(e) =>
                  setBirthYear(e.target.value === '' ? '' : parseInt(e.target.value, 10))
                }
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ec-color">Profile color</Label>
              <Input
                id="ec-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 cursor-pointer p-1"
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="ec-nickname">Nickname (optional)</Label>
            <Input
              id="ec-nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={80}
              placeholder="e.g. Annie"
            />
          </div>

          <Separator />

          {/* Delete confirmation. Inline, two-step — no accidental wipes. */}
          {confirmDelete ? (
            <div className="rounded-card border border-rose-200 bg-rose-50 p-3">
              <p className="text-sm font-medium text-rose-900">
                Delete {child.name}?
              </p>
              <p className="mt-0.5 text-xs text-rose-700">
                This removes the child and any paired devices. Location and
                timeline history becomes orphaned but isn't permanently lost
                (a sweeper reaps it later). You'll need a fresh pairing if
                you re-add them.
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmDelete(false)}
                  disabled={remove.isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-rose-600 hover:bg-rose-700"
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                >
                  {remove.isPending ? 'Deleting…' : `Delete ${child.name}`}
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className={cn(
                'flex items-center gap-1.5 self-start text-xs font-medium text-rose-700 hover:underline',
              )}
            >
              <Trash2 className="size-3.5" /> Delete this child
            </button>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => update.mutate()}
            disabled={!name.trim() || update.isPending}
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
